import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { DossierHttpService } from '@sc/data-access-http-services';
import { Dossier } from '@sc/data-access-models';

import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select } from '@ngxs/store';
import {
  DossierAdvancedFiltersInput,
  SearchDatesInput,
  SearchFittersInput,
} from '@sc/data-access-graphql';
import { map } from 'rxjs/operators';
import { FilterDossiersStates } from '../models/dossiers-states';
import {
  SearchDossiersState,
  SearchDossiersStateObj,
  SetDossiersSearchDossiersAction,
  SetFiltersSearchDossiersAction,
} from '../store';
import { AppointmentFilter } from '../types';

@Injectable({ providedIn: 'root' })
export class DossiersSandboxService {
  @Select(SearchDossiersState)
  searchDossiersState$: Observable<SearchDossiersStateObj>;

  @Select((state) => state.searchDossiers.search)
  search$: Observable<string>;

  @Select((state) => state.searchDossiers.dossiers)
  dossiers$: Observable<Dossier[]>;

  @Select((state) => state.searchDossiers.dossiersStates)
  dossiersStates$: Observable<FilterDossiersStates>;

  isLoadingDossiers$ = new BehaviorSubject<boolean>(false);

  constructor(private readonly dossierHttpService: DossierHttpService) {}

  @Dispatch()
  searchDossiers(
    searchText: string,
    states: FilterDossiersStates,
    creationDates?: SearchDatesInput,
    fitters?: SearchFittersInput[],
    advancedFilters?: DossierAdvancedFiltersInput
  ): Observable<SetDossiersSearchDossiersAction> {
    this.isLoadingDossiers$.next(true);

    this.updateFilters(searchText, states);

    const statesToSearch: AppointmentFilter[] = [];
    if (states?.inProgress) {
      statesToSearch.push('NEW');
    }
    if (states?.complete) {
      statesToSearch.push('COMPLETED');
    }
    if (states?.cancelled) {
      statesToSearch.push('CANCELLED');
    }
    if (states?.abandoned) {
      statesToSearch.push('ABANDONED');
    }
    if (states?.requested) {
      statesToSearch.push('REQUESTED');
    }
    if (states?.requestedAndValidated) {
      statesToSearch.push('REQUESTED_AND_VALIDATED');
    }
    if (statesToSearch.length) {
      return this.dossierHttpService
        .searchDossiers({
          searchText: searchText?.trim(),
          status: statesToSearch,
          creationDates,
          fitters,
          advancedFilters,
        })
        .pipe(
          map((dossiers) => {
            this.isLoadingDossiers$.next(false);
            return new SetDossiersSearchDossiersAction({
              dossiers: dossiers,
            });
          })
        );
    } else {
      this.isLoadingDossiers$.next(false);
      return of(
        new SetDossiersSearchDossiersAction({
          dossiers: [],
        })
      );
    }
  }

  @Dispatch()
  updateFilters(
    searchText: string,
    states: FilterDossiersStates
  ): Observable<SetFiltersSearchDossiersAction> {
    return of(
      new SetFiltersSearchDossiersAction({
        search: searchText,
        dossiersStates: states,
      })
    );
  }
}
